import {
  EventCardInterface,
  PastEventsProps,
} from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import {
  ArrowUpRightIcon,
  CalendarIcon,
  StarEmptyIcon,
  StarFilledIcon,
  StarHalfIcon,
  StarSolidIcon,
  TargetIcon,
} from "assets/icons";
import SideDrawerWrapper from "./SideDrawerWrapper";
import { useState } from "react";
import closeIcon from "assets/svg/close-icon.svg";
import moment from "moment";
import { getAppUrl } from "utils/subdomainDetection";
import { Link } from "react-router-dom";
import { useEnv } from "hooks/useEnv";
import { getEventTerminology } from "utils/eventTerminology";

// Format attendees count with appropriate suffix
function formatAttendees(count: number): string {
  if (!count) {
    return "0 attendee";
  }
  if (count >= 1_000_000) {
    const millions = count / 1_000_000;
    const rounded = Math.floor(millions * 10) / 10;
    return `${rounded.toLocaleString()}M+ attendees`;
  }
  if (count >= 1_000) {
    const thousands = Math.floor(count / 1_000) * 1_000;
    return `${thousands.toLocaleString()}+ attendees`;
  }
  return `${count.toLocaleString()} attendees`;
}

// Past Events Component
export default function PastEvents({ events }: PastEventsProps) {
  const [seeAllEvents, setSeeAllEvents] = useState(false);
  const { isLive } = useEnv();
  const { plural: pluralItemLabel } = getEventTerminology();

  return (
    <section className="mt-[40px] sm:mt-[64px]">
      <div className="w-full flex justify-between items-center">
        <h2 className="text-lg sm:text-xl font-monaMedium text-[#333333]">
          Past {pluralItemLabel}
        </h2>

        {events.length > 3 && (
          <button
            onClick={() => setSeeAllEvents(true)}
            className="text-sm sm:text-base px-3 py-[11px] gap-2 flex items-center rounded-lg bg-[#F2ECE2] text-[#20242A] font-monaMedium hover:bg-primary hover:text-white"
          >
            See all {events.length} {pluralItemLabel}{" "}
            <ArrowUpRightIcon stroke="currentColor" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-5 mt-5">
        {events.slice(0, 3).map((event, index) =>
          isLive ? (
            <EventCard key={index} {...event} isInteractive={false} />
          ) : (
            <Link key={index} to={getAppUrl(`/event-details/${event.id}`)}>
              <EventCard {...event} />
            </Link>
          ),
        )}
      </div>

      <SideDrawerWrapper
        openDrawer={seeAllEvents}
        handleCloseDrawer={() => setSeeAllEvents(false)}
      >
        <section className="h-full flex flex-col bg-[#FDF6EE]">
          {/* Header */}
          <div className="flex items-center justify-between py-3 px-5 border-b border-[#D5DBE1] bg-[#F2ECE2]">
            <h3 className="text-[20px] font-monaSemiBold text-[#30313D]">
              All {events.length} {pluralItemLabel}
            </h3>
            <button
              onClick={() => setSeeAllEvents(false)}
              className="cursor-pointer"
            >
              <img src={closeIcon} alt="close" />
            </button>
          </div>

          {/* Event List */}
          <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 gap-y-5">
            {events.map((event, index) =>
              isLive ? (
                <EventListItem key={index} {...event} />
              ) : (
                <Link key={index} to={getAppUrl(`/event-details/${event.id}`)}>
                  <EventListItem {...event} />
                </Link>
              ),
            )}
          </div>
        </section>
      </SideDrawerWrapper>
    </section>
  );
}

// Event Card Component
function EventCard({
  title,
  image,
  rating,
  attendees,
  engagementScore,
  isInteractive = true,
}: EventCardInterface & { isInteractive?: boolean }) {
  return (
    <div
      className={`bg-[#F2ECE2] p-3 rounded-[20px] transition-all duration-300 ${
        isInteractive
          ? "group cursor-pointer hover:shadow-lg hover:bg-white"
          : "cursor-default"
      }`}
    >
      <div className="rounded-xl overflow-hidden mb-3 w-full h-[180px] sm:h-[212px] bg-gray-200">
        <img
          src={image}
          alt={title}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isInteractive ? "group-hover:scale-110" : ""
          }`}
        />
      </div>
      <h3
        className={`font-monaMedium text-[#000000] mb-3 text-lg transition-colors duration-200 max-w-full truncate ${
          isInteractive ? "group-hover:text-orange-600" : ""
        }`}
      >
        {title}
      </h3>
      <div className="flex items-center justify-between text-sm">
        <Rating rating={rating || 0} />

        <span className="text-sm sm:text-base text-[#0088FF] font-monaMedium">
          {formatAttendees(attendees || 0)}
        </span>

        <div className="flex gap-2 items-center">
          <TargetIcon />
          <p className="text-[#545454] font-monaMedium text-sm">
            {engagementScore}%
          </p>
        </div>
      </div>
    </div>
  );
}

function Rating({ rating = 0 }: { rating?: number }) {
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.1 && rating % 1 <= 0.9;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    // Render filled stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarFilledIcon key={`filled-${i}`} width={16} height={16} />);
    }

    // Render half star if applicable
    if (hasHalfStar) {
      stars.push(<StarHalfIcon key="half" width={16} height={16} />);
    }

    // Render empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarEmptyIcon key={`empty-${i}`} width={16} height={16} />);
    }

    return stars;
  };

  const hasReviews = rating > 0;

  return (
    // <div className="">
    <div className="flex gap-3 items-center justify-end sm:justify-center">
      {hasReviews ? (
        <>
          <p className="text-sm sm:text-base font-monaMedium text-[#E3592E]">
            {rating.toFixed(1)}
          </p>
          <div className="flex gap-1">{renderStars()}</div>
        </>
      ) : (
        <p className="inline-block font-monaMedium text-sm text-[#E3592E]">
          No rating yet
        </p>
      )}
    </div>
    // </div>
  );
}

// Event List Item for Drawer
export function EventListItem({
  title,
  image,
  date,
  rating,
  attendees,
  engagementScore,
  ratingCount,
}: EventCardInterface) {
  return (
    <div className="flex gap-3 p-3 bg-[#F2ECE2] rounded-2xl">
      {/* Image */}
      <div className="w-[138px] h-[108px] rounded-[20px] overflow-hidden flex-shrink-0">
        <img src={image} alt={title} className="w-full h-full object-cover" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-monaMedium text-[#000000] text-lg truncate">
          {title}
        </h4>

        {/* Rating */}
        {/* <div className="flex items-center gap-2 mt-3">
          <span className="text-sm font-monaMedium text-[#E3592E]">
            {rating?.toFixed(1) || "0.0"}
          </span>
          <ListItemRating rating={rating || 0} />
        </div> */}
        <div className="flex items-center gap-1 mt-0">
          <ListItemRating rating={rating || 0} />

          {rating > 0 && ratingCount > 0 && (
            <p className="text-[#E3592E] font-sfMedium underline underline-offset-2">
              {ratingCount} review{ratingCount !== 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Attendees & Engagement */}
        <div className="flex items-center gap-3 mt-1">
          <span className="text-sm text-[#0088FF] font-monaMedium">
            {formatAttendees(attendees || 0)}
          </span>
          <div className="flex items-center gap-1">
            <TargetIcon width={14} height={14} />
            <span className="text-sm text-[#545454] font-monaMedium">
              {engagementScore || 0}%
            </span>
          </div>
        </div>

        {/* Date */}
        {date && (
          <div className="flex items-center gap-2 mt-1">
            <CalendarIcon width={14} height={14} />
            <span className="text-sm text-[#444D5A] font-monaMedium">
              {moment(date).format("MMM DD, YYYY")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact Rating for List Item
function ListItemRating({ rating = 0 }: { rating?: number }) {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.1 && rating % 1 <= 0.9;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <StarSolidIcon
        key={`filled-${i}`}
        width={14}
        height={14}
        fill="#E3592E"
      />,
    );
  }

  if (hasHalfStar) {
    stars.push(<StarHalfIcon key="half" width={14} height={14} />);
  }

  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <StarEmptyIcon
        key={`empty-${i}`}
        width={14}
        height={14}
        fill="#E5DFD5"
      />,
    );
  }

  const hasReviews = rating > 0;

  return (
    <>
      {hasReviews ? (
        <div className="flex items-center gap-2">
          <span className="text-sm font-monaMedium text-[#E3592E]">
            {rating?.toFixed(1) || "0.0"}
          </span>
          <div className="flex gap-0.5">{stars}</div>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-col w-fit">
          {/* <div className="flex gap-0.5">{stars}</div> */}
          <p className="font-monaMedium text-xs text-[#E3592E]">
            No rating yet
          </p>
        </div>
      )}
    </>
  );
}
